import { ChangeDetectionStrategy, Component, DestroyRef, computed, effect, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslatePipe } from '@ngx-translate/core';
import { ExecutionStats } from '../../../models/test-case.model';
import { TestCaseService } from '../../../services/test-case.service';

interface LegendItem {
  key: keyof Omit<ExecutionStats, 'passRate'>;
  modifier: string;
  labelKey: string;
  color: string;
}

@Component({
  selector: 'app-test-case-execution-stats',
  imports: [TranslatePipe],
  templateUrl: './test-case-execution-stats.component.html',
  styleUrl: './test-case-execution-stats.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TestCaseExecutionStatsComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly testCaseService = inject(TestCaseService);

  readonly projectId = input.required<string>();
  readonly testCaseId = input<string | null>(null);

  readonly stats = signal<ExecutionStats | null>(null);
  readonly loading = signal(false);
  readonly error = signal(false);

  readonly legend: readonly LegendItem[] = [
    {
      key: 'passed',
      modifier: 'passed',
      labelKey: 'testCases.form.executionStats.passed',
      color: 'var(--to-green-dot)',
    },
    {
      key: 'failed',
      modifier: 'failed',
      labelKey: 'testCases.form.executionStats.failed',
      color: 'var(--tc-exec-stats-red)',
    },
    {
      key: 'blocked',
      modifier: 'blocked',
      labelKey: 'testCases.form.executionStats.blocked',
      color: 'var(--to-amber-deep)',
    },
    {
      key: 'running',
      modifier: 'running',
      labelKey: 'testCases.form.executionStats.running',
      color: 'var(--to-blue-dot)',
    },
    {
      key: 'skipped',
      modifier: 'skipped',
      labelKey: 'testCases.form.executionStats.skipped',
      color: 'var(--to-text-faint)',
    },
    {
      key: 'notRun',
      modifier: 'not-run',
      labelKey: 'testCases.form.executionStats.notRun',
      color: 'var(--to-gray-dot)',
    },
  ];

  readonly total = computed(() => {
    const s = this.stats();
    return s ? this.legend.reduce((sum, item) => sum + (s[item.key] ?? 0), 0) : 0;
  });

  readonly hasData = computed(() => this.total() > 0);

  /**
   * conic-gradient für den Donut. Die Stopps werden aus den ungerundeten
   * Anteilen in Legendenreihenfolge kumuliert, damit kein Rundungsspalt
   * entsteht; der letzte Status füllt bis 100 %.
   */
  readonly donutBackground = computed(() => {
    const s = this.stats();
    const total = this.total();
    if (!s || total === 0) {
      return 'var(--to-border-soft)';
    }
    let start = 0;
    const segments = this.legend.map((item, index) => {
      const from = index === 0 ? '0' : `${start}%`;
      const end = index === this.legend.length - 1 ? 100 : start + ((s[item.key] ?? 0) / total) * 100;
      start = end;
      return `${item.color} ${from} ${end}%`;
    });
    return `conic-gradient(${segments.join(', ')})`;
  });

  readonly ariaParams = computed(() => {
    const s = this.stats();
    return {
      passRate: s?.passRate ?? 0,
      passed: s?.passed ?? 0,
      failed: s?.failed ?? 0,
      blocked: s?.blocked ?? 0,
      running: s?.running ?? 0,
      skipped: s?.skipped ?? 0,
      notRun: s?.notRun ?? 0,
    };
  });

  constructor() {
    effect(() => {
      const projectId = this.projectId();
      const testCaseId = this.testCaseId();
      if (!testCaseId) {
        this.stats.set(null);
        this.loading.set(false);
        this.error.set(false);
        return;
      }
      this.loadStats(projectId, testCaseId);
    });
  }

  private loadStats(projectId: string, testCaseId: string): void {
    this.loading.set(true);
    this.error.set(false);
    this.testCaseService
      .getExecutionStats(projectId, testCaseId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (stats) => {
          this.stats.set(stats);
          this.loading.set(false);
        },
        error: () => {
          this.stats.set(null);
          this.error.set(true);
          this.loading.set(false);
        },
      });
  }

  legendValue(item: LegendItem): number {
    return this.stats()?.[item.key] ?? 0;
  }
}
